import React from 'react';
import { motion } from 'framer-motion';
import { ChevronDown, Check } from 'lucide-react';
import { InteriorOption } from '../../types/interiorConfig';
import { useSelections } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
interface OptionsGalleryProps {
  options: InteriorOption[];
  onSelect: (optionId: string) => void;
  onClose: () => void;
  category?: string;
  itemName?: string;
}
// Mock price data - in a real app this would come from the backend
const getOptionPrice = (optionId: string): number => {
  const prices: Record<string, number> = {
    // Range Oven
    standard: 0,
    'level-1': 1500,
    'level-2': 2500,
    'level-3': 3500,
    'level-4': 4500,
    'black-electric': 2000,
    'black-gas': 2200,
    // Refrigerator
    'fridge-standard': 0,
    'fridge-french-door': 1800,
    'fridge-side-by-side': 1200,
    'fridge-counter-depth': 2400,
    'fridge-smart': 3500,
    'fridge-panel-ready': 2800,
    // Dishwasher
    'dw-standard': 0,
    'dw-quiet': 450,
    'dw-third-rack': 650,
    'dw-panel-ready': 850,
    'dw-smart': 1200,
    // Microwave
    'mw-standard': 0,
    'mw-over-range': 350,
    'mw-drawer': 1200,
    'mw-built-in': 800,
    // Countertop
    'ct-laminate': 0,
    'ct-granite': 4500,
    'ct-quartz': 5500,
    'ct-marble': 8500,
    'ct-butcher-block': 3200
  };
  return prices[optionId] ?? Math.floor(Math.random() * 3000) + 500;
};
export function OptionsGallery({
  options,
  onSelect,
  onClose,
  category = 'Kitchen',
  itemName = 'Option'
}: OptionsGalleryProps) {
  const {
    addSelection
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const handleSelect = (option: InteriorOption) => {
    const price = getOptionPrice(option.id);
    // Add to selections
    addSelection({
      category,
      itemName,
      optionName: option.name,
      optionId: option.id,
      price,
      thumbnail: option.thumbnail
    });
    // Call original onSelect
    onSelect(option.id);
  };
  return <motion.div initial={{
    y: '100%'
  }} animate={{
    y: 0
  }} exit={{
    y: '100%'
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 200
  }} className="absolute bottom-[60px] left-[320px] right-0 z-30 flex justify-center pointer-events-none px-6" data-id="element-450">
      <div className={`backdrop-blur-xl border rounded-t-2xl shadow-2xl pointer-events-auto w-full max-w-6xl overflow-hidden transition-colors duration-300 ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-white/40'}`} data-id="element-451">
        {/* Header */}
        <div className={`flex items-center justify-between px-6 py-3 border-b ${isDark ? 'border-white/5 bg-white/5' : 'border-gray-200/50 bg-gray-50/50'}`} data-id="element-452">
          <div className="flex items-center gap-3" data-id="element-453">
            <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-white/50' : 'text-gray-500'}`} data-id="element-454">
              Select Option
            </span>
            <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-500/20 text-teal-600'}`} data-id="element-455">
              {category} › {itemName}
            </span>
          </div>
          <button onClick={onClose} className={`p-1 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} data-id="element-456">
            <ChevronDown className="w-4 h-4" data-id="element-457" />
          </button>
        </div>

        {/* Scrollable Gallery */}
        <div className="p-6 overflow-x-auto custom-scrollbar" data-id="element-458">
          <div className="flex items-start gap-4 min-w-min pb-2" data-id="element-459">
            {options.map((option, index) => {
            const price = getOptionPrice(option.id);
            const isIncluded = price === 0;
            return <button key={option.id} onClick={() => handleSelect(option)} className="group relative flex flex-col items-center gap-3 transition-all flex-shrink-0" data-id="element-460">
                  {/* Image Container */}
                  <div className={`relative w-32 h-32 rounded-lg overflow-hidden border-2 transition-all duration-300 ${option.selected ? isDark ? 'border-cyan-500 shadow-[0_0_20px_rgba(6,182,212,0.3)]' : 'border-teal-500 shadow-[0_0_20px_rgba(20,184,166,0.3)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}`} data-id="element-461">
                    {option.thumbnail ? <img src={option.thumbnail} alt={option.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-462" /> : <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`} data-id="element-463">
                        <span className={`text-xs ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-464">
                          No Image
                        </span>
                      </div>}

                    {/* Selected Overlay */}
                    {option.selected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-465">
                        <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-466">
                          <Check className="w-4 h-4 text-white" data-id="element-467" />
                        </div>
                      </div>}

                    {/* Number Badge */}
                    <div className={`absolute top-2 left-2 w-5 h-5 backdrop-blur-sm rounded flex items-center justify-center text-[10px] font-bold border ${isDark ? 'bg-black/60 text-white border-white/10' : 'bg-white/80 text-gray-700 border-gray-200'}`} data-id="element-468">
                      {index + 1}
                    </div>
                  </div>

                  {/* Label */}
                  <div className="flex flex-col items-center gap-1 w-32" data-id="element-469">
                    <span className={`text-xs font-medium transition-colors truncate w-full text-center ${option.selected ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-white/70' : 'text-gray-600'}`} data-id="element-470">
                      {option.name}
                    </span>

                    {/* Price Tag - More prominent */}
                    <div className={`px-3 py-1 rounded-full text-xs font-bold ${isIncluded ? 'bg-emerald-500 text-white' : isDark ? 'bg-cyan-500 text-white' : 'bg-teal-500 text-white'}`} data-id="element-471">
                      {isIncluded ? 'Included' : `+$${price.toLocaleString()}`}
                    </div>

                    {option.selected && <span className={`text-[10px] font-medium uppercase tracking-wider ${isDark ? 'text-cyan-500/60' : 'text-teal-500/60'}`} data-id="element-472">
                        Selected
                      </span>}
                  </div>
                </button>;
          })}
          </div>
        </div>
      </div>
    </motion.div>;
}